<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref } from 'vue'
import { ofetch } from 'ofetch'
import { useHead } from '@unhead/vue'
import AuthBrandHeader from '@/components/auth/AuthBrandHeader.vue'
import PlayAgentGame from '@/components/voice-agent/PlayAgentGame.vue'
import CandidateConsent from '@/views/recruiting/CandidateConsent.vue'
import CandidateFinished from '@/views/recruiting/CandidateFinished.vue'
import { $api } from '@/utils/api'
import type { SessionExitDetails } from '@/components/voice-session/flow'
import type { GameExitReason } from '@/types/gameExit'
import { Modules } from '@/types/generated'
import PublicLinkData = Modules.Recruiting.Data.PublicLinkData
import CandidateLinkStatusEnum = Modules.Recruiting.Enums.CandidateLinkStatusEnum

// `name` задан явно: автогенератор имён (`getRouteName` в `vite.config.ts`) режет PascalCase
// по границе «строчная→заглавная», а `CToken` такой границы не имеет и схлопнулся бы в `ctoken`.
definePage({
  name: 'c-token',
  meta: {
    public: true,
    layout: 'blank',
  },
})

/**
 * Экраны страницы. Прежняя версия: `intro` → `consent` → `game` → `terminal`. Новая
 * (`newSessionScreen` ссылки): `intro` → `session` (подготовка и разговор, `SessionFlow`) →
 * `terminal`. `loading` и отказы до загрузки ссылки — всегда прежние: версия ещё неизвестна.
 */
type Screen = 'loading' | 'intro' | 'consent' | 'game' | 'session' | 'terminal'

type TerminalKind =
  | 'finished'
  | 'already_completed'
  | 'not_found'
  | 'session_conflict'
  | 'link_revoked'
  | 'link_expired'
  | 'attempts_exhausted'
  | 'unavailable'
  | 'rate_limited'
  | 'aborted'
  | 'generic'

/**
 * Машинные коды продуктовых отказов бэкенда (все — 422, тело `{message, code}`).
 * `recruiting_quota_exhausted` и `recruiting_start_unavailable` сознательно ведут на один
 * нейтральный экран: кандидату не показывают ни биллинг организации, ни внутренние причины.
 */
const REFUSAL_BY_CODE: Record<string, TerminalKind> = {
  recruiting_session_conflict: 'session_conflict',
  recruiting_link_revoked: 'link_revoked',
  recruiting_link_expired: 'link_expired',
  recruiting_attempts_exhausted: 'attempts_exhausted',
  recruiting_quota_exhausted: 'unavailable',
  recruiting_start_unavailable: 'unavailable',
}

const TERMINAL_STYLE: Record<TerminalKind, { icon: string; color: string }> = {
  finished: { icon: 'tabler-circle-check', color: 'success' },
  already_completed: { icon: 'tabler-circle-check', color: 'success' },
  not_found: { icon: 'tabler-link-off', color: 'error' },
  session_conflict: { icon: 'tabler-user-exclamation', color: 'warning' },
  link_revoked: { icon: 'tabler-lock', color: 'warning' },
  link_expired: { icon: 'tabler-clock-off', color: 'warning' },
  attempts_exhausted: { icon: 'tabler-repeat-off', color: 'warning' },
  unavailable: { icon: 'tabler-alert-circle', color: 'warning' },
  rate_limited: { icon: 'tabler-hourglass', color: 'warning' },
  aborted: { icon: 'tabler-plug-connected-x', color: 'warning' },
  generic: { icon: 'tabler-alert-triangle', color: 'error' },
}

// Экраны новой версии — только отдельными чанками: страница с прежним потоком не должна тянуть ни
// их, ни шрифты и стили дерева `voice-session`, которые они подключают.
const loadSessionIntro = () => import('@/components/voice-session/entry/SessionIntro.vue')
const loadSessionFlow = () => import('@/components/voice-session/SessionFlow.vue')
const loadSessionFinished = () => import('@/components/voice-session/entry/SessionFinished.vue')

const LazySessionIntro = defineAsyncComponent(loadSessionIntro)
const LazySessionFlow = defineAsyncComponent(loadSessionFlow)
const LazySessionFinished = defineAsyncComponent(loadSessionFinished)

const route = useRoute('c-token')
const { t, locale } = useI18n()

const token = String(route.params.token)
const screen = ref<Screen>('loading')
const link = ref<PublicLinkData | null>(null)
const terminal = ref<TerminalKind>('generic')
const endNotSent = ref(false)
const gameId = ref('')
const starting = ref(false)

/** Страница показывает новую версию экрана. Решается один раз, по загруженной ссылке. */
const newScreen = ref(false)

/**
 * Отметка согласия на вступлении новой версии. Живёт в памяти страницы: на сервер согласие уходит
 * только вместе со стартом на подготовке (`createGame`).
 */
const consent = ref(false)

/**
 * Попытка уже начата (обычно — F5 посреди интервью): игра станет известна только на старте, а
 * подготовка уже должна сказать, что время идёт, и звать вернуться в разговор.
 */
const linkInProgress = computed(() => link.value?.status === CandidateLinkStatusEnum.InProgress)

const requirements = computed<string[]>(() => {
  if (!link.value)
    return []

  return [
    t('candidate-page.requirement_mic'),
    link.value.isCameraRequired
      ? t('candidate-page.requirement_camera_required')
      : t('candidate-page.requirement_camera_optional'),
    t('candidate-page.requirement_browser'),
    t('candidate-page.requirement_network'),
  ]
})

const terminalView = computed(() => ({
  ...TERMINAL_STYLE[terminal.value],
  title: t(`candidate-page.terminal.${terminal.value}.title`),
  text: t(`candidate-page.terminal.${terminal.value}.text`),
}))

// Реактивно: язык страницы выставляется только после загрузки ссылки.
useHead({ title: computed(() => t('candidate-page.document_title')) })

function showTerminal(kind: TerminalKind): void {
  terminal.value = kind
  screen.value = 'terminal'
}

/**
 * 404 приходит и на неизвестный токен, и на ссылку чужой организации, и на выключённую фичу —
 * все три случая кандидат видит как «ссылка не найдена». Это намеренное нераскрытие:
 * отдельный экран «подбор выключен» подтверждал бы, что такая ссылка вообще существует.
 */
function refusalFromError(error: any): TerminalKind {
  const code = error?.response?._data?.code ?? error?.data?.code
  const status = error?.response?.status ?? error?.status

  if (typeof code === 'string' && REFUSAL_BY_CODE[code])
    return REFUSAL_BY_CODE[code]

  if (status === 404)
    return 'not_found'

  if (status === 429)
    return 'rate_limited'

  return 'generic'
}

/**
 * Ссылку в статусе `in_progress` пропускаем к старту независимо от `canStart`: незавершённая
 * попытка уже сожгла свой слот, поэтому `canStart` у неё false, но `startAttempt()` на сервере
 * проверяет resume раньше лимита попыток и вернёт ту же игру. Иначе перезагрузка страницы
 * посреди интервью показывала бы «попытки закончились».
 *
 * Одной этой маршрутизации мало: resume возвращает попытку, только пока её игра НЕ терминальна.
 * Поэтому `PlayAgentGame` получает здесь `end-session-on-page-exit="false"` — иначе `pagehide`
 * того же F5 успевал бы закрыть игру keepalive-запросом, и вернувшийся кандидат упирался бы
 * в 422 `recruiting_attempts_exhausted` при уже списанной квоте. Новая версия уход со страницы
 * не обрабатывает вовсе: `end-session` там уходит только по «Завершить» и по окончании времени.
 */
function entryScreen(data: PublicLinkData): void {
  if (data.status === CandidateLinkStatusEnum.Revoked) {
    showTerminal('link_revoked')

    return
  }

  if (data.status === CandidateLinkStatusEnum.Completed) {
    showTerminal('already_completed')

    return
  }

  if (data.status === CandidateLinkStatusEnum.InProgress) {
    screen.value = 'intro'

    return
  }

  if (data.isExpired) {
    showTerminal('link_expired')

    return
  }

  if (!data.canStart) {
    showTerminal('attempts_exhausted')

    return
  }

  screen.value = 'intro'
}

/**
 * Guard дергает `/sanctum/csrf-cookie` fire-and-forget (`plugins/1.router/guards.ts`), поэтому
 * к моменту POST куки может не быть. Ждём её явно: без `XSRF-TOKEN` запрос упадёт 419,
 * а ретраить продуктовый POST нельзя.
 */
async function ensureCsrfToken(): Promise<void> {
  if (useCookie('XSRF-TOKEN').value)
    return

  await ofetch.raw('/sanctum/csrf-cookie')
}

/**
 * Чанки новой версии грузятся до того, как страница уйдёт с экрана загрузки: так вступление
 * появляется сразу, без пустого кадра, а подготовка — сразу по «Далее». Не загрузились (обычно —
 * выкатка сменила имена чанков под открытой вкладкой) — кандидат проходит интервью прежним
 * экраном: сервер от версии экрана не зависит, а пустая страница оставила бы его без интервью.
 */
async function loadNewScreens(): Promise<boolean> {
  try {
    await Promise.all([loadSessionIntro(), loadSessionFlow(), loadSessionFinished()])

    return true
  }
  catch (error) {
    console.error('Failed to load the new session screen, falling back to the current one:', error)

    return false
  }
}

async function loadLink(): Promise<void> {
  try {
    const data = await $api<PublicLinkData>(`/recruiting/links/${encodeURIComponent(token)}`, { retry: 0 })

    // Язык страницы — язык организации, в котором бэкенд отдал и текст согласия (после
    // фолбэка), поэтому интерфейс и согласие не разъезжаются. Меняется только локаль i18n,
    // cookie `language` не пишется: навязывать язык остальному приложению незачем. Экраны
    // до загрузки ссылки (loading и отказы 404/429) остаются на текущей локали браузера.
    locale.value = data.locale
    newScreen.value = data.newSessionScreen && await loadNewScreens()
    link.value = data
    entryScreen(data)
  }
  catch (error) {
    showTerminal(refusalFromError(error))
  }
}

/**
 * Начать попытку или вернуть незавершённую: `POST /start` с согласием → `game_id`.
 * `retry: 0` обязателен: `$api` ретраит 409/419/425, и продуктовый отказ был бы молча повторён
 * до того, как кандидат его увидит.
 */
async function requestStart(): Promise<string> {
  await ensureCsrfToken()

  const response = await $api<{ game_id: string }>(
    `/recruiting/links/${encodeURIComponent(token)}/start`,
    { method: 'POST', body: { consent: true }, retry: 0 },
  )

  return response.game_id
}

async function startInterview(): Promise<void> {
  if (starting.value)
    return

  starting.value = true
  try {
    gameId.value = await requestStart()
    screen.value = 'game'
  }
  catch (error) {
    showTerminal(refusalFromError(error))
  }
  finally {
    starting.value = false
  }
}

/**
 * Новая версия: игру создаёт кнопка старта на подготовке (`SessionFlow`), согласие уходит вместе
 * с ней — до старта на сервере нет ни игры, ни попытки, ни согласия. Ссылка `in_progress` приходит
 * сюда тем же путём, и старт возвращает её незавершённую игру. Отказ — экран отказа страницы
 * (поток при этом размонтируется); reject говорит потоку, что игры нет.
 */
async function createGame(): Promise<string> {
  try {
    return await requestStart()
  }
  catch (error) {
    showTerminal(refusalFromError(error))

    throw error
  }
}

function handleGameExit(reason: GameExitReason, details?: SessionExitDetails): void {
  endNotSent.value = details?.endNotSent ?? false
  showTerminal(reason === 'aborted' ? 'aborted' : 'finished')
}

onMounted(loadLink)
</script>

<template>
  <div class="candidate-page">
    <!--
      Новая версия: без логотипа и цветов организации, тема — светлая (у кандидата без
      переключателя), разговор — тёмный. Уход со страницы разговор не завершает.
    -->
    <template v-if="newScreen && link">
      <LazySessionIntro
        v-if="screen === 'intro'"
        v-model:consent="consent"
        entry="candidate"
        :link="link"
        @next="screen = 'session'"
      />

      <LazySessionFlow
        v-else-if="screen === 'session'"
        entry="candidate"
        :game-id="null"
        :link="link"
        :create-game="createGame"
        :resuming="linkInProgress"
        :exit-handler="handleGameExit"
      />

      <LazySessionFinished
        v-else-if="screen === 'terminal'"
        entry="candidate"
        :outcome="terminal"
        :end-not-sent="endNotSent"
      />
    </template>

    <PlayAgentGame
      v-else-if="screen === 'game'"
      :game-id="gameId"
      :exit-handler="handleGameExit"
      :show-preparation="false"
      :end-session-on-page-exit="false"
    />

    <div
      v-else
      class="candidate-page__shell"
    >
      <div class="candidate-page__brand">
        <AuthBrandHeader />
      </div>

      <div
        v-if="screen === 'loading'"
        class="candidate-page__center"
      >
        <VProgressCircular
          indeterminate
          size="48"
          color="primary"
        />
      </div>

      <VCard
        v-else-if="screen === 'intro' && link"
        class="candidate-card"
      >
        <VCardText>
          <h1 class="text-h5 mb-2">
            {{ t('candidate-page.greeting', { name: link.candidateName }) }}
          </h1>

          <p class="text-body-1 mb-2">
            {{ t('candidate-page.intro_lead', { organization: link.organizationName, vacancy: link.vacancyTitle }) }}
          </p>

          <p class="text-body-1 mb-6">
            {{ t('candidate-page.duration', { minutes: link.durationMinutes }) }}
          </p>

          <h2 class="text-h6 mb-2">
            {{ t('candidate-page.requirements_title') }}
          </h2>

          <VList
            density="compact"
            class="candidate-page__requirements"
          >
            <VListItem
              v-for="requirement in requirements"
              :key="requirement"
              :title="requirement"
            >
              <template #prepend>
                <VIcon
                  icon="tabler-point-filled"
                  size="14"
                  class="me-2"
                />
              </template>
            </VListItem>
          </VList>
        </VCardText>

        <VCardActions class="candidate-page__actions">
          <VSpacer />
          <VBtn
            color="primary"
            variant="elevated"
            size="large"
            @click="screen = 'consent'"
          >
            {{ t('candidate-page.continue') }}
          </VBtn>
        </VCardActions>
      </VCard>

      <CandidateConsent
        v-else-if="screen === 'consent' && link"
        :consent-text="link.consentText"
        :loading="starting"
        @accept="startInterview"
        @back="screen = 'intro'"
      />

      <CandidateFinished
        v-else-if="screen === 'terminal'"
        :title="terminalView.title"
        :text="terminalView.text"
        :icon="terminalView.icon"
        :color="terminalView.color"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.candidate-page__shell {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem 1rem;
  min-block-size: 100vh;
  justify-content: center;
  align-items: center;
}

.candidate-page__brand {
  color: rgb(var(--v-global-theme-primary));
}

.candidate-page__center {
  display: flex;
  justify-content: center;
}

.candidate-card {
  inline-size: 100%;
  max-inline-size: 720px;
}

.candidate-page__requirements {
  background: transparent;
}

.candidate-page__actions {
  padding-block-end: 1rem;
  padding-inline: 1rem;
}
</style>
